import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { WhatsappLogo, ShieldCheck } from "@phosphor-icons/react";

export default function Settings() {
  const { user } = useAuth();
  const isSuper = (user?.permissions || []).includes("*");
  const [wa, setWa] = useState(null);
  const [history, setHistory] = useState([]);
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("Hello from OM GOLD!");
  const [cfg, setCfg] = useState({ phone_number_id: "", access_token: "", api_version: "v25.0", reminder_template: "", offer_template: "", template_language: "en_US" });
  const [cfgInfo, setCfgInfo] = useState(null);

  const loadWa = () => api.get("/settings/whatsapp").then(({ data }) => setWa(data)).catch(() => {});
  useEffect(() => {
    loadWa();
    api.get("/auth/login-history").then(({ data }) => setHistory(data)).catch(() => {});
    if (isSuper) api.get("/settings/whatsapp/config").then(({ data }) => { setCfgInfo(data); setCfg((c) => ({ ...c, phone_number_id: data.phone_number_id || "", api_version: data.api_version || "v25.0", reminder_template: data.reminder_template || "", offer_template: data.offer_template || "", template_language: data.template_language || "en_US" })); }).catch(() => {});
  }, [isSuper]);

  const saveConfig = async () => {
    try {
      const payload = { phone_number_id: cfg.phone_number_id, api_version: cfg.api_version, reminder_template: cfg.reminder_template, offer_template: cfg.offer_template, template_language: cfg.template_language };
      if (cfg.access_token) payload.access_token = cfg.access_token;
      const { data } = await api.post("/settings/whatsapp/config", payload);
      toast.success(`WhatsApp ${data.mode === "live" ? "is now LIVE" : "saved (stub)"}`);
      setCfg({ ...cfg, access_token: "" });
      loadWa();
      api.get("/settings/whatsapp/config").then(({ data }) => setCfgInfo(data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const testWa = async () => {
    if (!to) return toast.error("Enter a phone number with country code (e.g. +9198...)");
    try {
      const { data } = await api.post("/settings/whatsapp/test", { to, message: msg });
      if (data.stub) toast.info("WhatsApp is in STUB mode — message logged, not sent. Configure credentials to go live.");
      else toast.success("WhatsApp message sent!");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Settings" subtitle="Integration configuration & account activity." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]"><WhatsappLogo size={20} className="text-[#25D366]" /> WhatsApp Business</div>
            <Badge className={wa?.configured ? "bg-[#E8F5E9] text-[#2D6A4F] border-0" : "bg-[#FFF8E1] text-[#B8860B] border-0"}>
              {wa?.configured ? "LIVE" : "STUB MODE"}
            </Badge>
          </div>
          <p className="text-sm text-[#8A8A8A] mb-4">
            {wa?.configured
              ? "Credentials configured. Messages send via WhatsApp Business Cloud API."
              : "Add your WhatsApp Business number ID and API token below to enable live sending. Until then messages are logged only."}
          </p>

          {isSuper && (
            <div className="border border-[#E6E2D6] rounded-md p-3 mb-4 bg-[#FDFBF7]">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#8A6D2F] mb-2">Configure (Super Admin)</div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Business Phone Number ID</Label>
                  <Input value={cfg.phone_number_id} onChange={(e) => setCfg({ ...cfg, phone_number_id: e.target.value })} placeholder="e.g. 123456789012345" className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-phone-id" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B]">API Token / Key {cfgInfo?.has_token && <span className="text-[#2D6A4F]">(saved {cfgInfo.access_token_masked})</span>}</Label>
                  <Input type="password" value={cfg.access_token} onChange={(e) => setCfg({ ...cfg, access_token: e.target.value })} placeholder={cfgInfo?.has_token ? "Leave blank to keep existing" : "Permanent access token"} className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-token" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs text-[#6B6B6B]">Reminder template</Label><Input value={cfg.reminder_template} onChange={(e) => setCfg({ ...cfg, reminder_template: e.target.value })} placeholder="installment_reminder" className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-reminder-template" /></div>
                  <div><Label className="text-xs text-[#6B6B6B]">Offer template</Label><Input value={cfg.offer_template} onChange={(e) => setCfg({ ...cfg, offer_template: e.target.value })} placeholder="omgold_offer" className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-offer-template" /></div>
                  <div><Label className="text-xs text-[#6B6B6B]">Language</Label><Input value={cfg.template_language} onChange={(e) => setCfg({ ...cfg, template_language: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-template-lang" /></div>
                </div>
                <p className="text-[11px] text-[#8A8A8A]">Reminder template body uses {"{{1}}"} name, {"{{2}}"} amount, {"{{3}}"} scheme, {"{{4}}"} account no, {"{{5}}"} due date. Offer template uses {"{{1}}"} name, {"{{2}}"} offer text.</p>
                <div>
                  <Button onClick={saveConfig} variant="outline" className="border-[#E6E2D6]" data-testid="wa-save-config">Save Configuration</Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Recipient (E.164)</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+919812345678" className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-to" />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Message</Label>
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-msg" />
            </div>
            <Button onClick={testWa} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="wa-test-btn">Send Test Message</Button>
          </div>
        </Card>

        <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A] mb-4"><ShieldCheck size={20} className="text-[#C5A059]" /> Account & Login History</div>
          <div className="text-sm space-y-1 mb-4">
            <div><span className="text-[#8A8A8A]">Name:</span> {user?.name}</div>
            <div><span className="text-[#8A8A8A]">Email:</span> {user?.email}</div>
            <div><span className="text-[#8A8A8A]">Role:</span> <span className="text-[#8A6D2F]">{user?.role_name}</span></div>
            <div><span className="text-[#8A8A8A]">Discount Limit:</span> {user?.max_discount_percent}%</div>
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] mb-2">Recent Logins</div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-xs border-b border-[#F2EBE1] pb-2">
                <span className="text-[#4A4A4A]">{fmtDate(h.at)}</span>
                <span className="text-[#8A8A8A]">{h.ip_address}</span>
              </div>
            ))}
            {history.length === 0 && <div className="text-xs text-[#8A8A8A]">No login history yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
